import { useState } from "react";
import { Box, Typography, Dialog, DialogContent } from "@mui/material";
import { QrCode, CalendarMonth } from "@mui/icons-material";
import QRCode from "react-qr-code";
import type { IStreamDetails } from "../../../../types/memorial-types";
import {
  type CalendarEvent,
  getGoogleCalendarUrl,
} from "../../../../types/AddtoCalendarutils";
import whatsappIcon from "../icons/whatapp.png";
import facebookIcon from "../icons/facebook.png";
import mailIcon from "../icons/mail.png";
import shareIcon from "../icons/share.png";

interface ShareSidebarProps {
  streamData: IStreamDetails;
  calendarEvent?: CalendarEvent | null;
}

export default function ShareSidebar({ streamData, calendarEvent }: ShareSidebarProps) {
  const [openQr, setOpenQr] = useState(false);

  const shareUrl = streamData?.website_stream_link ?? "";
  const title = streamData?.title ?? "";

  const handleWhatsAppShare = () => {
    if (!shareUrl) return;
    const text = encodeURIComponent(
      `It is with great sadness that we share the livestream link for the funeral ${title}.\n\nYou can view it here: ${shareUrl}`,
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleFacebookShare = () => {
    if (!shareUrl) return;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank",
    );
  };

  const handleEmailShare = () => {
    if (!shareUrl) return;
    const subject = encodeURIComponent(
      `Funeral Service for ${title} - Livestream Link`,
    );
    const body = encodeURIComponent(
      `Dear friends and family,\n\nWe are sharing the livestream link for the funeral service of ${title}\n\nYou can view the livestream here: ${shareUrl}\n\nThank you for your thoughts and support during this difficult time.\n\nSincerely,`,
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handleGenericShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
      } catch {
        // user cancelled or unsupported
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          pt: 1,
          minWidth: 72,
        }}
      >
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{ mb: 0.5, fontSize: "0.85rem" }}
        >
          Share:
        </Typography>

        {/* QR — no image provided, use MUI icon styled to match */}
        <Box
          sx={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setOpenQr(true)}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: "#fff",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              "&:hover": { opacity: 0.8 },
            }}
          >
            <QrCode sx={{ fontSize: 32, color: "#333" }} />
          </Box>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.5, fontSize: "0.7rem" }}
          >
            Scan
          </Typography>
        </Box>

        {/* WhatsApp */}
        <IconItem
          src={whatsappIcon}
          label="WhatsApp"
          onClick={handleWhatsAppShare}
        />

        {/* Facebook */}
        <IconItem
          src={facebookIcon}
          label="Facebook"
          onClick={handleFacebookShare}
        />

        <Box
          sx={{ textAlign: "center", cursor: "pointer" }}
          onClick={handleEmailShare}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              bgcolor: "#fff",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              "&:hover": { opacity: 0.8 },
            }}
          >
            <Box
              component="img"
              src={mailIcon}
              alt="Mail"
              sx={{ width: 34, height: 34, objectFit: "contain" }}
            />
          </Box>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.5, fontSize: "0.7rem" }}
          >
            Mail
          </Typography>
        </Box>

        <Box
          sx={{ textAlign: "center", cursor: "pointer" }}
          onClick={handleGenericShare}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: "#fff",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              "&:hover": { opacity: 0.8 },
            }}
          >
            <Box
              component="img"
              src={shareIcon}
              alt="Share"
              sx={{
                width: 30,
                height: 30,
                objectFit: "contain",
                filter: "brightness(10)",
              }}
            />
          </Box>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.5, fontSize: "0.7rem" }}
          >
            Share
          </Typography>
        </Box>

        {calendarEvent && (
          <Box
            sx={{ textAlign: "center", cursor: "pointer" }}
            onClick={() =>
              window.open(
                getGoogleCalendarUrl(calendarEvent),
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#fff",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                "&:hover": { opacity: 0.8 },
              }}
            >
              <CalendarMonth sx={{ fontSize: 32, color: "#333" }} />
            </Box>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 0.5, fontSize: "0.7rem" }}
            >
              Calendar
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={openQr} onClose={() => setOpenQr(false)}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <QRCode value={shareUrl} size={200} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function IconItem({
  src,
  label,
  onClick,
}: {
  src: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <Box sx={{ textAlign: "center", cursor: "pointer" }} onClick={onClick}>
      <Box
        sx={{
          width: 56,
          height: 56,
          bgcolor: "#fff",
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          "&:hover": { opacity: 0.8 },
        }}
      >
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{
            width: 32,
            height: 32,
            objectFit: "contain",
          }}
        />
      </Box>

      <Typography
        variant="caption"
        display="block"
        sx={{ mt: 0.5, fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
    </Box>
  );
}
